import React from "react";
import { TransformedCustomViewContainer } from "../../components/transformed_custom_view";
import { defaultConfig } from "./default_config";
import { PivotControl } from "../../constants/pivot_control_options";
import { DatePivotParams } from "../../entities/date_pivot_params";
import { SupplyWalkEditableCustomControls } from "./custom_controls";
import { getSupplyWalkEditableViewMatrix } from "./get_matrix";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { useApi } from "../../contexts/app/provider";
import { SysMessagesContext } from "../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../contexts/sys_messages/type";
import {
  SaveApprovedPOsDocument,
  PoActionType,
} from "../../services/api/__generated__/backend_gateway-types";
import { SupplyWalkEditsContext } from "../../contexts/supply_walk_edits/provider";
import { SupplyWalkEditableNestedDetails } from "./nested_details";

// TODO: Implement cascadeRecalculateNested helper when we have a way to modify Arrow table data

export const SupplyWalkEditableContainer = () => {
  const { apiService } = useApi();
  const { mainRun, lastSuccessfulRun } = React.useContext(VersionedDataContext);
  const { addSystemMessage } = React.useContext(SysMessagesContext);
  const supplyGuard = React.useContext(SupplyWalkEditsContext);

  // Persistent storage is now managed by the SupplyWalkEditsContext

  // Row edit state storage (temporary edits while editing)
  // This replaces the module-level globalRowEditState to properly scope state to this route
  const rowEditStateRef = React.useRef(
    new Map<
      string,
      {
        isEditing: boolean;
        editValues: Record<string, string>;
        committedValues: Record<string, string>;
        originalValues: Record<string, string>;
        listeners: Set<() => void>;
      }
    >()
  ).current;

  // Track pending saves to debounce
  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = React.useRef(false);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Clear all edits when run changes to prevent cross-run contamination
  React.useEffect(() => {
    // Clear all edits when run changes
    rowEditStateRef.clear();
  }, [
    mainRun?.runSettings?.runAttemptId,
    lastSuccessfulRun?.runSettings?.runAttemptId,
    rowEditStateRef,
  ]);

  // Save approved PO action to backend (backend handles merge logic)
  const saveApprovedPOsToS3 = React.useCallback(
    async (rowData: any, isApproving: boolean) => {
      try {
        const runToUse = mainRun || lastSuccessfulRun;
        if (!runToUse) {
          return;
        }

        // Get the row key to look up edits
        const runAttemptId =
          mainRun?.runSettings?.runAttemptId ||
          lastSuccessfulRun?.runSettings?.runAttemptId ||
          "no-run";
        const rowKey = `${rowData.purchase_order_id || "unknown"}-${rowData.sku || "unknown"}-${rowData.location || "unknown"}-${rowData.supplier || "unknown"}-${runAttemptId}`;
        const editData = supplyGuard?.getEdit?.(rowKey);

        // Extract edited values if they exist
        const editedValues = editData?.committedValues || {};

        // Send individual PO action to backend
        await apiService.getClient().mutate({
          mutation: SaveApprovedPOsDocument,
          variables: {
            runSettings: {
              inputId: runToUse.runSettings.inputId,
              workflowId: runToUse.runSettings.workflowId,
              settingsNameAndVersion:
                runToUse.runSettings.settingsNameAndVersion,
              runAttemptId: runToUse.runSettings.runAttemptId,
            },
            poAction: {
              purchase_order_id: rowData.purchase_order_id,
              sku: rowData.sku,
              location: rowData.location,
              supplier: rowData.supplier,
              // Use edited values if they exist, otherwise use original values
              quantity: editedValues.quantity
                ? parseFloat(editedValues.quantity)
                : rowData.quantity,
              total_cost: editedValues.total_cost
                ? parseFloat(editedValues.total_cost)
                : rowData.total_cost,
              unit_cost: editedValues.unit_cost
                ? parseFloat(editedValues.unit_cost)
                : rowData.unit_cost,
              order_placed_date:
                editedValues.order_placed_date || rowData.order_placed_date,
              order_delivery_date:
                editedValues.order_delivery_date || rowData.order_delivery_date,
              action: isApproving
                ? PoActionType.Approve
                : PoActionType.Unapprove,
            },
          },
        });
      } catch (error) {
        throw error; // Re-throw to handle in calling function
      }
    },
    [mainRun, lastSuccessfulRun, apiService, supplyGuard]
  );

  // Handle checkbox toggle with S3 save and table reload
  const handleApprovedToggle = React.useCallback(
    (rowKey: string, rowData: any, checked: boolean) => {
      // Update the edit context immediately for navigation guard
      if (supplyGuard?.setRowApproved) {
        supplyGuard.setRowApproved(rowKey, checked);
      }

      // Update approved row count in real-time
      if (checked) {
        supplyGuard?.incrementApprovedRowCount?.();
      } else {
        supplyGuard?.decrementApprovedRowCount?.();
      }

      // Clear any pending save timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce the actual save to avoid too many S3 writes
      saveTimeoutRef.current = setTimeout(() => {
        if (isSavingRef.current) {
          // If already saving, schedule another save after this one completes
          return;
        }

        isSavingRef.current = true;

        // Send individual PO action to backend
        saveApprovedPOsToS3(rowData, checked)
          .then(() => {
            isSavingRef.current = false;
            // Data will be fresh on next navigation or refresh
          })
          .catch((error) => {
            isSavingRef.current = false;

            const errorMsg =
              error?.message || error?.toString() || "Unknown error";
            addSystemMessage({
              level: SysMessageLevel.DANGER,
              message: `Failed to save approved POs: ${errorMsg}`,
            });
          });
      }, 1000); // 1 second debounce
    },
    [saveApprovedPOsToS3, addSystemMessage, supplyGuard]
  );

  const CustomControlsWithApprovedCount = React.useCallback((props: any) => {
    return <SupplyWalkEditableCustomControls {...props} />;
  }, []);

  return (
    <TransformedCustomViewContainer
      viewControllerConfig={
        {
          datasetName: "po_plan_edit",
          transformId: "supply_walk_editable",
          folder: "outputs",
          title: "Purchase Order Inbox (Beta)",
          dateHierarchyColumnFilter: null,
          excludedSegmentationsFilter: [],
          measureColumnFilter: null,
          excludedFilterColumnFilter: [],
          columnHeaderMap: {
            purchase_order_id: "PO ID",
            sku: "SKU",
            order_placed_date: "PO day",
            order_delivery_date: "ETA day",
            total_cost: "total cost",
            unit_cost: "unit cost",
          },
          showControls: [
            PivotControl.Download,
            PivotControl.ShowSettings,
            PivotControl.StoreAsDefault,
          ],
          customControlPanel: CustomControlsWithApprovedCount,
          paramsToTransformArgs: (
            params: DatePivotParams
          ): Record<string, any> => {
            return {
              date_field: params.dateHierarchy,
              min_date: params.minDate,
              max_date: params.maxDate,
              group_by_fields: [],
              filters: params.filters,
            };
          },
          tableType: "flat",
          getMatrix: (params: any) => {
            // Try multiple ways to get the current status
            let currentStatus = "";

            // Method 1: Check params.filters
            if (params.filters) {
              const statusFilter = params.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            // Method 2: Check if status is directly in params
            if (!currentStatus && params.status) {
              currentStatus = params.status;
            }

            // Method 3: Check viewParams.filters
            if (!currentStatus && params.viewParams?.filters) {
              const statusFilter = params.viewParams.filters.find(
                (filter: any) => filter[0] === "status"
              );
              currentStatus = statusFilter ? statusFilter[2][0] : "";
            }

            return getSupplyWalkEditableViewMatrix({
              ...params,
              viewParams: { ...(params?.viewParams || {}) },
              mainRun,
              lastSuccessfulRun,
              NestedDetailsComponent: SupplyWalkEditableNestedDetails,
              currentStatus,
              rowEditStateRef,
              onApprovedToggle: handleApprovedToggle,
              supplyGuard,
            });
          },
        } as any
      }
      defaultConfig={defaultConfig}
    />
  );
};
